import type { ArtifactDecision } from "@/types/artifacts.ts";
import { toUrnString } from "@/types/urn.ts";
import type { StagedDecision } from "./artifactDecisions.ts";
import type { OmniArtifact } from "./selectOmniArtifacts.ts";

/**
 * The canvas URNs a finished review should take down: the forms it answered.
 *
 * A form is an input, and once the review is submitted there is nothing left to
 * put into it — the user pressed Done on the questions, not on looking at them,
 * so leaving the form open on the canvas beside the thread reads as still
 * pending. Heavy cards are deliberately left alone: a draft that was just sent
 * is still worth reading.
 *
 * This is only the commit, which is when a review's cards resolve: inside a
 * review every decision is staged, so nothing comes down until then. A
 * lone artifact still fires on click and takes its own canvas card down — see
 * `ArtifactBiscuit`'s `activate`.
 *
 * Native has no canvas to take anything down, so it simply has no caller.
 */
export function spentFormUrns(
  review: readonly OmniArtifact[],
  sent: readonly ArtifactDecision[],
): string[] {
  // Only an answer spends a form: one skipped, or given a note, is still waiting.
  const answeredIds = new Set(
    sent.filter((decision) => decision.kind === "form").map((decision) => decision.artifactId),
  );
  return review
    .filter((artifact) => artifact.artifactType === "userResponse" && answeredIds.has(artifact.id))
    .map((artifact) => toUrnString({ type: artifact.artifactType, id: artifact.id }));
}

/**
 * How many of the review's cards the user has settled, for its "N decided"
 * counter.
 *
 * A card counts once it carries a staged decision — an action, a note or a form
 * answer — or once it stops awaiting the user, which drops it from `live` while
 * the review holds on to it. The second half is not redundant with the first: a
 * card can resolve without a staged decision behind it, having been answered on
 * the canvas or from another device while the review stood.
 */
export function countDecidedCards({
  review,
  live,
  decisions,
}: {
  review: readonly OmniArtifact[];
  live: readonly OmniArtifact[];
  decisions: ReadonlyMap<string, StagedDecision>;
}) {
  const stillWaiting = new Set(live.map((a) => a.id));
  return review.filter((a) => !stillWaiting.has(a.id) || decisions.has(a.id)).length;
}

/**
 * The cards a review pages through, which is *not* the same as the artifacts
 * still awaiting a decision.
 *
 * Deciding a draft resolves it, so it leaves `selectOmniArtifacts` immediately.
 * Paging off the live set as it shrinks would renumber the queue under the user
 * and strand whoever is looking at the card they just sent: their pager would
 * disappear along with its arrows (BIG-7171). So a decided card stays in the
 * review, showing itself as decided, until the review as a whole is over.
 *
 * A review opens and closes on different signals, deliberately:
 *   - It opens only while the server says the thread awaits the user, the gate
 *     that keeps a completed turn from resurfacing a stale artifact (BIG-5581).
 *   - It closes only when nothing is left awaiting a decision. Sending a draft
 *     starts an agent run, so the thread stops awaiting the user as soon as the
 *     first card is decided; closing on that signal would strand the user with
 *     two drafts left and no carousel to reach them.
 *
 * Returns `previous` unchanged when nothing moved, so a caller can store this in
 * state without re-rendering on every unrelated thread update.
 */
export function nextReviewArtifacts({
  previous,
  live,
  turnCards,
  isAwaitingUser,
}: {
  /** The review as it stands. */
  previous: readonly OmniArtifact[];
  /** Cards in this turn still awaiting a decision. */
  live: readonly OmniArtifact[];
  /** Every card in this turn, decided or not — where held cards re-read
   *  themselves so a sent draft doesn't render frozen as still sendable. */
  turnCards: readonly OmniArtifact[];
  /** The server's "this thread is waiting on you" signal. */
  isAwaitingUser: boolean;
}): readonly OmniArtifact[] {
  const noReview = previous.length === 0;
  if (noReview && !isAwaitingUser) return previous;
  if (live.length === 0) return noReview ? previous : [];

  const liveById = new Map(live.map((a) => [a.id, a]));
  // Sharing no card with the review means this is a different one (a new turn):
  // replace outright, or the finished review's cards trail into it.
  const isSameReview = previous.some((a) => liveById.has(a.id));
  if (!isSameReview) return live;

  const freshById = new Map(turnCards.map((a) => [a.id, a]));
  const carried = previous.map((a) => freshById.get(a.id) ?? a);
  const previousIds = new Set(previous.map((a) => a.id));
  // An agent can add a fourth draft while the user is mid-review.
  const added = live.filter((a) => !previousIds.has(a.id));

  // Identity matters: this feeds `useState`, and a new array every render would
  // loop. Nothing moved if no card was added and every carried object is the
  // one already held.
  const unchanged = added.length === 0 && carried.every((artifact, i) => artifact === previous[i]);
  return unchanged ? previous : [...carried, ...added];
}
